import {openResourceSection} from '../../core/resourceEvents';
import {
	CLIENT_AP_SECTION_EVENT,
	CLIENT_HOME_SECTION_EVENT,
	CLIENT_REFRESH_EVENT,
	CLIENT_SEARCH_EVENT,
	CLIENT_SITE_SECTION_EVENT,
} from './events';

/**
 * Open a Clients section, and reload it when that is what was asked for.
 *
 * Opening is not refreshing. "Refresh the client list" reaches the same
 * section as "show clients", and if the accordion is already open there is
 * nothing for arriving to change - the table would sit there with the same
 * rows and the agent would report success. The catalogue marks the refresh
 * with highlight_action, which travels in the section command, so the two
 * are told apart here rather than being the same thing twice.
 *
 * @param {string} eventName section event identifier
 * @param {string} sectionId section being opened
 * @param {Set<string>} allowed sections this handler serves
 * @param {object} command section command
 * @return {Promise<void>} completion acknowledgement
 */
async function openClientSection(eventName, sectionId, allowed, command) {
	const seen = await openResourceSection(eventName, sectionId, allowed);
	if (command?.params?.action_target === 'refresh') await refreshClients();
	// Carried back rather than awaited and dropped. The section says
	// what the page actually opened; a wrapper returning nothing
	// turns that into an empty acknowledgement, and the backend then
	// cannot tell a section that opened from one that did not.
	return seen;
}

const HOME_SECTIONS = new Set(['home.clients_active']);
const SITE_SECTIONS = new Set(['site.clients_active']);
const AP_SECTIONS = new Set(['ap.clients_active']);

/**
 * Open the Clients accordion on the Home Dashboard, on the Active tab.
 *
 * Every one of these does two things, not one: expand the accordion and
 * select Active. The tab lives in local state inside the accordion and
 * resets whenever it remounts, so arriving is never enough - Historical may
 * be the tab that is showing.
 *
 * @param {object} command section command
 * @return {Promise<void>} completion acknowledgement
 */
export function openHomeClientSection(command) {
	return openClientSection(
		CLIENT_HOME_SECTION_EVENT, 'home.clients_active', HOME_SECTIONS, command,
	);
}

/**
 * Open the Clients accordion on a Site Dashboard, on the Active tab.
 * @param {object} command section command
 * @return {Promise<void>} completion acknowledgement
 */
export function openSiteClientSection(command) {
	return openClientSection(
		CLIENT_SITE_SECTION_EVENT, 'site.clients_active', SITE_SECTIONS, command,
	);
}

/**
 * Open the Clients accordion on an Access Point Dashboard, on the Active tab.
 * @param {object} command section command
 * @return {Promise<void>} completion acknowledgement
 */
export function openApClientSection(command) {
	return openClientSection(
		CLIENT_AP_SECTION_EVENT, 'ap.clients_active', AP_SECTIONS, command,
	);
}

/**
 * Put a query into the Active Clients search box.
 *
 * This is the whole of client filtering. The filter modal collects a Site, a
 * band and an AP and then sends only the Site, so the search box is the one
 * control that reaches the seven columns people actually name. It also means
 * a request narrows by one thing at a time: the server matches a single
 * substring, never two terms.
 *
 * @param {string} query what to search for
 * @return {Promise<void>} completion acknowledgement
 */
export function applyClientSearch(query) {
	// On a client's own page there is no list to narrow, and nothing to
	// narrow it to: the page is already that one client. Narrowing exists to
	// bring the record into view, and here it is the view - so this is
	// satisfied by where we are standing rather than by a search box that
	// does not exist on this route.
	if (window.location.pathname.toLowerCase() === '/client') {
		return Promise.resolve();
	}
	return dispatchUntilAnswered(CLIENT_SEARCH_EVENT, {query: String(query || '')});
}

/**
 * Reload the Active Clients table.
 * @return {Promise<void>} completion acknowledgement
 */
export function refreshClients() {
	return dispatchUntilAnswered(CLIENT_REFRESH_EVENT, {});
}

// Long enough for the table to mount after its section reports itself open,
// and short enough that a control already on screen is not kept waiting.
const ANSWER_TIMEOUT_MS = 8000;
const RETRY_MS = 120;

/**
 * Send an event until the table answers it, or give up saying so.
 *
 * The table mounts when its accordion expands, and the accordion reports
 * itself open before React has finished mounting the children - so a single
 * send arrives at nobody.
 *
 * @param {string} eventName resource event identifier
 * @param {object} detail event payload
 * @return {Promise<void>} acknowledgement
 */
function dispatchUntilAnswered(eventName, detail) {
	return new Promise((resolve, reject) => {
		let settled = false;
		// Declared before the first send, not after it. The table answers
		// synchronously when it is already mounted, so finish() can run
		// during that first send - and a `const retry` assigned below would
		// still be in its dead zone, turning a search that worked into
		// "Cannot access 'retry' before initialization". The modal
		// dispatcher keeps the same shape for the same reason.
		let retry = null;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			if (retry !== null) clearInterval(retry);
			callback();
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('client_table_unavailable'))),
			ANSWER_TIMEOUT_MS,
		);
		const send = () => {
			if (settled) return;
			window.dispatchEvent(new CustomEvent(eventName, {
				detail: {
					...detail,
					complete: () => finish(resolve),
					fail: (reason) => finish(
						() => reject(new Error(reason || 'client_action_failed')),
					),
				},
			}));
		};
		send();
		if (!settled) retry = setInterval(send, RETRY_MS);
	});
}
